/* istanbul ignore file */

import { Module } from 'vuex';
import { AppState } from '..';
import { prefixValues } from '@/helpers/util';
import { AnalyticsEvent } from '@/services/analytics/models';
import { Product, Value, WebInteraction } from '@/services/analytics/platforms/adobe-analytics/models';
import { AdobeCartAddEvent, AdobeCartViewEvent } from '@/services/analytics/platforms/adobe-analytics/adobe-events';

export interface AdobeModuleState {
  isFromBag: boolean;
  isMakeItMyOwn: boolean;

  // The data layer may not be available 
  // when page loads, so we temporarily 
  // store any Adobe Analytics events here.
  adobeDataLayerEvents: AnalyticsEvent[];

  // When app loads, no properties have been 
  // sent until there is an AdobePageLoadEvent. 
  // We only want to send the initial properties 
  // when the first page load event that occurs
  haveInitialPropertiesBeenSent: boolean;

  // In order to reduce Adobe Data Layer 
  // server calls, when a 'linkClicked' event
  // triggers a subsequent 'pageloaded' event,
  // the 'linkClicked' event is stored until 
  // the subsequent 'pageloaded' event is fired
  linkClickedEvent: WebInteraction | null;

  // Add-to-bag will be stored until subsequest CartPageLoadEvent is fired.
  addToBagEvent: AdobeCartAddEvent | null;

  // Bag-view event to be stored until subsequent CartPageLoadEvent is fired.
  bagViewEvent: AdobeCartViewEvent | null;

  // Upsell impressions to be stored until CartPageLoadEvent is fired
  upsellImpression: Product[] | null;

  // Only show upsell impression if list has changed since last user login
  hasUpsellImpressionChanged: boolean;

  registrationCompleteEvent: Value | null;
  rewardsEnrollEvent: Value | null;

  // We only want to send certain pageLoaded 
  // events once per session
  hasHomePageBeenViewed: boolean;
  hasNutritionCalcPageBeenViewed: boolean;
}

const modulePrefix = 'adobe/';
const MUTATION_NAMES = {
  SET_IS_FROM_BAG: 'setIsFromBag',
  SET_IS_MAKE_IT_MY_OWN: 'setIsMakeItMyOwn',
  SET_ADOBE_DATA_LAYER_EVENTS: 'setAdobeDataLayerEvents',
  SET_INITIAL_APP_LOAD_PROPERTIES_HAVE_BEEN_SENT: 'setInitialAppLoadProperitesHaveBeenSent',
  SET_LINK_CLICKED_EVENT: 'setLinkedClickEvent',
  SET_REGISTRATION_COMPLETE_EVENT: 'setRegistrationCompleteEvent',
  SET_ADD_TO_BAG_EVENT: 'setAddToBagEvent',
  SET_BAG_VIEW_EVENT: 'setBagViewEvent',
  SET_UPSELL_IMPRESSION: 'setUpsellImpression',
  SET_HAS_UPSELL_IMPRESSION_CHANGED: 'setHasUpsellImpressionChanged',
  SET_REWARDS_ENROLL_EVENT: 'setRewardsEnrollEvent',
  SET_HAS_HOME_PAGE_BEEN_VIEWED: 'setHasHomePageBeenViewd',
  SET_HAS_NUTRITION_CALC_PAGE_BEEN_VIEWED: 'setHasNutritionCalcPageBeenViewed',
};

export const MUTATIONS = prefixValues(MUTATION_NAMES, modulePrefix);

export function makeAdobeModule(): Module<AdobeModuleState, AppState> {
  return {
    namespaced: true,
    state: {
      isFromBag: false,
      isMakeItMyOwn: false,
      adobeDataLayerEvents: [], 
      haveInitialPropertiesBeenSent: false, 
      linkClickedEvent: null,
      addToBagEvent: null,
      bagViewEvent: null,
      upsellImpression: null,
      hasUpsellImpressionChanged: false,
      registrationCompleteEvent: null,
      rewardsEnrollEvent: null,
      hasHomePageBeenViewed: false,
      hasNutritionCalcPageBeenViewed: false,
    },
    mutations: {
      [MUTATION_NAMES.SET_IS_FROM_BAG]: (state, isFromBag: boolean) => {
        state.isFromBag = isFromBag;
      },
      [MUTATION_NAMES.SET_IS_MAKE_IT_MY_OWN]: (state, isMakeItMyOwn: boolean) => {
        state.isMakeItMyOwn = isMakeItMyOwn;
      },
      [MUTATION_NAMES.SET_ADOBE_DATA_LAYER_EVENTS]: (state, adobeDataLayerEvent: AnalyticsEvent) => {
        state.adobeDataLayerEvents.push(adobeDataLayerEvent);
      },
      [MUTATION_NAMES.SET_INITIAL_APP_LOAD_PROPERTIES_HAVE_BEEN_SENT]: (state, initialPropertiesSent) => {
        state.haveInitialPropertiesBeenSent = initialPropertiesSent; 
      },
      [MUTATION_NAMES.SET_LINK_CLICKED_EVENT]: (state, webInteraction: WebInteraction | null) => {
        state.linkClickedEvent = webInteraction; 
      },
      [MUTATION_NAMES.SET_REGISTRATION_COMPLETE_EVENT]: (state, accountManagementAnalytics: Value | null) => {
        state.registrationCompleteEvent = accountManagementAnalytics; 
      },
      [MUTATION_NAMES.SET_REWARDS_ENROLL_EVENT]: (state, accountManagementAnalytics: Value | null) => {
        state.rewardsEnrollEvent = accountManagementAnalytics;
      },
      [MUTATION_NAMES.SET_HAS_HOME_PAGE_BEEN_VIEWED]: (state, hasHomePageBeenViewed: boolean) => {
        state.hasHomePageBeenViewed = hasHomePageBeenViewed;
      },
      [MUTATION_NAMES.SET_HAS_NUTRITION_CALC_PAGE_BEEN_VIEWED]: (state, hasNutritionCalcPageBeenViewed: boolean) => {
        state.hasNutritionCalcPageBeenViewed = hasNutritionCalcPageBeenViewed;
      },
      [MUTATION_NAMES.SET_ADD_TO_BAG_EVENT]: (state, addToBagEvent: AdobeCartAddEvent) => {
        state.addToBagEvent = addToBagEvent;
      },
      [MUTATION_NAMES.SET_BAG_VIEW_EVENT]: (state, bagViewEvent: AdobeCartViewEvent) => {
        state.bagViewEvent = bagViewEvent;
      },
      [MUTATION_NAMES.SET_UPSELL_IMPRESSION]: (state, upsellImpression: Product[]) => {
        state.upsellImpression = upsellImpression;
      },
      [MUTATION_NAMES.SET_HAS_UPSELL_IMPRESSION_CHANGED]: (state, hasUpsellImpressionChanged: boolean) => {
        state.hasUpsellImpressionChanged = hasUpsellImpressionChanged;
      },
    },
  };
}
